/* eslint-disable import/prefer-default-export */
import React from 'react';
import PropTypes from 'prop-types';

import Panel from '../../../components/Panel.jsx';

import MetricTableHeader from '../../../components/table/MetricTableHeader.jsx';

import {
    useCurationConfigValue
} from '../../../hooks/widgetConfiguration.js';

import {
    useRegisterSlideElementsForPPTExport
} from '../../../hooks/useExport.js';

import {
    useListAccountSavedSearches,
    useListAccountQuidTopics
} from '../hooks/useListeningQueryPersistence.js';

import {
    listeningDefinitions
} from './enums/listeningDefinitions.jsx';

import useLandscapeChangeRedirectUrl from '../../../hooks/useLandscapeChangeRedirectUrl.js';

import {
    SavedSearchTableEmpty
} from './SavedSearchListWidget.jsx';
import RIQLoading from '../../../components/RIQLoading.jsx';

import useMetrics from '../../../hooks/useMetrics.js';

AccountListeningListWidget.propTypes = {
    instanceId: PropTypes.string.isRequired
};

function onContactUsClick(e) {
    e.preventDefault();
    if (window.Intercom) {
        window.Intercom('show');
    }
}

export function AccountListeningListWidget({instanceId}) {

    const {
        title,
        topicOrThemeUnavailable
    } = listeningDefinitions[instanceId] || {};

    useRegisterSlideElementsForPPTExport(instanceId, {
        header: {
            title
        }
    });

    const {data: allSavedSearches, isLoading: isLoadingAllSavedSearches} = useListAccountSavedSearches();

    const plan = useCurationConfigValue('plan');
    const planSavedSearchesLimit = plan?.max_listening_saved_searches || 0;
    const atLimit = (_.size(allSavedSearches) >= planSavedSearchesLimit);
    const savedSearchEmptyFallback = <SavedSearchTableEmpty isAtLimit={atLimit}/>;
    const numberOfSavedSearchesLandscapes = Number(_.size(_.uniq(_.map(allSavedSearches, 'landscapeId')))) || '0';

    const {data: allQuidTopics} = useListAccountQuidTopics();

    const hasCompanionMode = RivalIQ.state.account.get('hasCompanionMode');
    const numberOfQuidTopicsLandscapes = Number(_.size(_.uniq(_.map(allQuidTopics, 'landscapeId')))) || '0';
    const quidTopicEmptyFallback = (
        <tr>
            <td
                className='tlw-p-10 tlw-text-center'
                colSpan='7'>
                <h3 className='tlw-text-lg'>This account doesn&rsquo;t have any connected Quid Monitor Topics.</h3>
                <p className='tlw-text-sm'>Connect Topics to easily access and monitor your social listening data.</p>
            </td>
        </tr>);

    let metricRequests = {};

    if (hasCompanionMode) {
        metricRequests = {
            topicName: {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
                dimensions: ['listeningQueryId'],
                measure: 'topicName',
                filters: {
                    listening: {
                        listeningQueryTypes: [
                            RivalIQ.enums.listeningQueryTypes.QuidTopic.id
                        ]
                    }
                }
            },
            themeNamesDisplay: {
                dataSourceId: RivalIQ.metrics.new.enums.DataSourceIds.QUID_TOPICS,
                dimensions: ['listeningQueryId'],
                measure: 'themeNamesDisplay',
                filters: {
                    listening: {
                        listeningQueryTypes: [
                            RivalIQ.enums.listeningQueryTypes.QuidTopic.id
                        ]
                    }
                }
            }
        };
    }
    const {data: metricResults, isLoading: isMetricsLoading} = useMetrics(metricRequests);
    const results = RivalIQ.metrics.new.MetricResponse.groupResponsesByFixedDimensions(metricResults);

    if (!isMetricsLoading && results.length > 0) {
        _.forEach(allQuidTopics, topic => {
            const topicMetric = _.find(results, result => result.topicName[0] === topic.id);
            const searchable = (topicMetric.topicName[1] !== null);

            topic.name = searchable ? topicMetric.topicName[1] : <span className='font-italic tlw-max-w-[30ch] tlw-truncate tlw-opacity-50'>{topicOrThemeUnavailable}</span>;
            topic.themeName = searchable ? topicMetric.themeNamesDisplay[1] : '';
        });
    }

    return (
        <>
            <Summary
                savedSearchesUsed={_.size(allSavedSearches)}
                planSavedSearchesLimit={planSavedSearchesLimit}
                numberOfSavedSearchesLandscapes={numberOfSavedSearchesLandscapes}
                hasCompanionMode={hasCompanionMode}
                numberOfQuidTopics={_.size(allQuidTopics)}
                numberOfQuidTopicsLandscapes={numberOfQuidTopicsLandscapes}
            />
            {planSavedSearchesLimit > 0 && <Panel
                instanceId={instanceId}
                title={title('Saved Searches')}
                showViewMore={false}
                isExportable={true}
            >
                <AccountListeningTable
                    allResults={allSavedSearches}
                    isLoading={isLoadingAllSavedSearches}
                    emptyFallback={savedSearchEmptyFallback}
                    redirect={'/listening'}
                />
            </Panel>}
            {hasCompanionMode && <Panel
                instanceId={instanceId}
                title={title('Quid Monitor Topics')}
                showViewMore={false}
                isExportable={true}
            >
                <AccountListeningTable
                    allResults={allQuidTopics}
                    isLoading={isMetricsLoading}
                    emptyFallback={quidTopicEmptyFallback}
                    hasCompanionMode={hasCompanionMode}
                    redirect={'/listening/topics'}
                />
            </Panel>}
        </>
    );
}

function AccountListeningTableRow({savedSearchId, listeningName, themeName, landscapeId, landscapeName, createdBy, lastViewed, hasCompanionMode, redirect}) {

    useLandscapeChangeRedirectUrl('/listening');

    function onClick() {
        RivalIQ.execute('landscape:load', landscapeId, redirect);
    }

    return (
        <tr
            className='tlw-h-[47px] tlw-border-0 tlw-border-t tlw-border-solid tlw-border-gray-200 hover_tlw-bg-blue-50'
        >

            <td
                className='tlw-p-4'>
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <div
                        className='tlw-flex-grow tlw-whitespace-nowrap'
                    >
                        {listeningName}
                    </div>
                </div>
            </td>
            {hasCompanionMode && <td
                className='tlw-p-4'>
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <div
                        className='tlw-block tlw-max-w-96 tlw-truncate'
                    >
                        {themeName}
                    </div>
                </div>
            </td>}
            <td
                className='tlw-p-4'
            >
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <button
                        onClick={onClick}
                        className='tlw-flex-grow tlw-whitespace-nowrap tlw-border-none tlw-bg-transparent tlw-p-0 tlw-text-left tlw-text-blue-400 hover_tlw-text-blue-450 hover_tlw-underline focus_tlw-text-blue-450 focus_tlw-underline'
                    >
                        {landscapeName}
                    </button>
                </div>
            </td>
            <td
                className='tlw-p-4'>
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <div
                        className='tlw-flex-grow tlw-whitespace-nowrap'
                    >
                        {createdBy}
                    </div>
                </div>
            </td>
            {!hasCompanionMode && <td
                className='tlw-p-4'>
                <div
                    className='tlw-flex tlw-flex-row tlw-text-md tlw-font-normal tlw-text-gray-900'>
                    <div
                        className='tlw-flex-grow tlw-whitespace-nowrap'
                    >
                        {lastViewed}
                    </div>
                </div>
            </td>}
        </tr>
    );
}

function AccountListeningTable({allResults, isLoading, emptyFallback, hasCompanionMode = false, redirect}) {

    if (isLoading) {
        return <RIQLoading/>;
    }

    let tableBody;
    if (emptyFallback && _.isEmpty(allResults)) {
        tableBody = emptyFallback;
    } else {
        tableBody = _.map(allResults, ({id, name, themeName, landscapeId, landscapeName, firstName, lastName, lastViewed}) => {
            const rowData = {
                savedSearchId: id,
                listeningName: name,
                themeName: themeName,
                landscapeId,
                landscapeName,
                createdBy: _.startCase(`${firstName} ${lastName}`),
                lastViewed: lastViewed ? moment(lastViewed).format('MMM D, YYYY') : ''
            };

            return (
                <AccountListeningTableRow
                    key={id}
                    {...rowData}
                    hasCompanionMode={hasCompanionMode}
                    redirect={redirect}
                />
            );
        });
    }

    return (
        <div className='tlw-overflow-x-auto max-sm_tlw-w-full max-sm_tlw-overflow-y-hidden'>
            <table className='tlw-w-full'>
                <thead>
                    <tr className='tlw-bg-gray-150'>
                        <MetricTableHeader
                            title= {hasCompanionMode ? `Topic Name` : `Search Name`}
                            className='max-[767px]_tlw-whitespace-nowrap'
                        />
                        {hasCompanionMode && <MetricTableHeader
                            title='Theme Name'
                            titleFormatter={null}
                            className='max-sm_tlw-min-w-[260px] max-sm_tlw-whitespace-nowrap md_tlw-w-[260px]'
                            description={listeningDefinitions['account-saved-searches']?.themeName}
                        />}
                        <MetricTableHeader
                            title='Landscape Name'
                            titleFormatter={null}
                            className='max-sm_tlw-min-w-[260px] max-sm_tlw-whitespace-nowrap md_tlw-w-[260px]'
                            description={listeningDefinitions['account-saved-searches']?.landscapeName}
                        />
                        <MetricTableHeader
                            title='Created By'
                            titleFormatter={null}
                            className='max-sm_tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap md_tlw-w-[150px]'
                            description={listeningDefinitions['account-saved-searches']?.createdBy}
                        />
                        {!hasCompanionMode && <MetricTableHeader
                            title='Last Viewed'
                            titleFormatter={null}
                            className='max-sm_tlw-min-w-[150px] max-sm_tlw-whitespace-nowrap md_tlw-w-[150px]'
                            description={listeningDefinitions['account-saved-searches']?.lastViewed}
                        />}
                    </tr>
                </thead>
                <tbody>{tableBody}</tbody>
            </table>
        </div>
    );
}

function Summary({savedSearchesUsed, planSavedSearchesLimit, numberOfSavedSearchesLandscapes, hasCompanionMode, numberOfQuidTopics, numberOfQuidTopicsLandscapes}) {

    const {
        title,
        Description
    } = listeningDefinitions['account-listening-summary'] || {};


    return (
        <Panel
            instanceId='account-listening-summary'
            title={title}
        >
            <div
                className='tlw-flex tlw-flex-row tlw-items-center tlw-p-7.5 tlw-text-md tlw-font-normal tlw-text-gray-900'
            >
                <Description
                    savedSearchesUsed={savedSearchesUsed}
                    planSavedSearchesLimit={planSavedSearchesLimit}
                    numberOfSavedSearchesLandscapes={numberOfSavedSearchesLandscapes}
                    onContactUsClick={onContactUsClick}
                    hasCompanionMode={hasCompanionMode}
                    numberOfQuidTopics={numberOfQuidTopics}
                    numberOfQuidTopicsLandscapes={numberOfQuidTopicsLandscapes}
                />
            </div>
        </Panel>
    );
}
